import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, RefreshCw } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export default function VerificationPending() {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  // Auto-poll every 15s so admin verification flips seamlessly.
  useEffect(() => {
    const t = setInterval(() => {
      refreshMe();
    }, 15000);
    return () => clearInterval(t);
  }, [refreshMe]);

  useEffect(() => {
    if (user?.is_verified) {
      navigate("/dashboard", { replace: true });
    }
  }, [user?.is_verified, navigate]);

  const handleRefresh = async () => {
    setChecking(true);
    await refreshMe();
    setChecking(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="mb-page flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg mb-rise">
        <div className="flex justify-center">
          <BrandLogo className="h-10" />
        </div>

        <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-mb-card">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50"
            aria-hidden
          >
            <Clock className="h-7 w-7 text-amber-500" />
          </div>

          <h1
            className="mt-6 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
            data-testid="verification-pending-title"
          >
            Your PRC license is under review.
          </h1>
          <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-gray-500">
            Thanks for signing up, {user?.full_name?.split(" ")[0] || "broker"}.
            Our team is verifying the details on your PRC ID. You&apos;ll get access
            to the MatchBroker dashboard the moment you&apos;re approved.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-3 text-left sm:grid-cols-2">
            <InfoRow label="Account" value={user?.email} testId="pending-email" />
            <InfoRow
              label="PRC License"
              value={user?.prc_license_number}
              testId="pending-license"
            />
          </div>

          <div className="mt-10 flex flex-col-reverse items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={handleLogout}
              className="mb-focus-ring h-11 rounded-xl border-gray-200 bg-white px-6 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              data-testid="pending-logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </Button>
            <Button
              type="button"
              onClick={handleRefresh}
              disabled={checking}
              className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A]"
              data-testid="pending-refresh-button"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${checking ? "animate-spin" : ""}`}
              />
              Check status
            </Button>
          </div>
        </div>

        <p className="mt-8 text-center text-xs font-medium uppercase tracking-wider text-gray-400">
          Typical review time · under 24 hours
        </p>
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </div>
      <div
        className="mt-1 truncate text-sm font-medium text-gray-800"
        data-testid={testId}
      >
        {value || "—"}
      </div>
    </div>
  );
}
